import ProxyServerConnector from './connectors/ProxyServerConnector';

type ParsedDirectToken = {
    exp: number;
};

const parseJWT = (token: string) => {
    if (!token) {
        return null;
    }

    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
        window
            .atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join(''),
    );

    return JSON.parse(jsonPayload);
};

class AuthService extends ProxyServerConnector {
    gateway: ProxyServerConnector;

    directToken = '';

    parsedDirectToken: ParsedDirectToken | null = null;

    directTokenPendingRef: Promise<string> | null = null;

    constructor() {
        super('');
        this.gateway = new ProxyServerConnector('/authentication', GATEWAY_PREFIX);
    }

    private setCurrentToken(token: string) {
        this.directToken = token;
        this.parsedDirectToken = parseJWT(token);
        this.directTokenPendingRef = null;
    }

    private isDirectTokenValid = () => {
        if (this.parsedDirectToken === null) {
            return false;
        }

        const { exp } = this.parsedDirectToken;
        const currentTimeInSeconds = Math.floor(Date.now() / 1000);
        const leftTimeToExpire = exp - currentTimeInSeconds;
        const allowedTimeUntilExpire = 60 * 20;

        return leftTimeToExpire > allowedTimeUntilExpire;
    };

    getDirectToken = (bynderToken: string): Promise<string> => {
        if (this.directToken && this.isDirectTokenValid()) {
            return Promise.resolve(this.directToken);
        }

        if (this.directTokenPendingRef) {
            return this.directTokenPendingRef;
        }

        this.directTokenPendingRef = this.rotateToken(bynderToken).then(({ json, status }) => {
            if (status === 200) {
                this.setCurrentToken(json.accessToken);

                return this.directToken;
            }

            this.setCurrentToken('');

            return '';
        });

        return this.directTokenPendingRef;
    };

    refreshToken() {
        return this.gateway.get<{ status: number; identityAccessToken: string }>('/refresh');
    }

    private rotateToken(accessToken: string) {
        return this.gateway.post<{ accessToken: string }>('/rotate-token', { accessToken });
    }

    exchangeBynderToIdentityToken(data) {
        return this.gateway.post('/exchange-bynder-to-identity-token', data);
    }

    registerBynderUser(data) {
        return this.gateway.post('/register-bynder-user', data);
    }

    checkToken() {
        return this.gateway.get('/check-token');
    }
}

export default new AuthService();
